<template lang="pug">
.register-body
  //- select filter type
  .register-row
    .register-group
      .register-key
        span.key-name 予定範囲（利用者グループ）
        span.validation-label 必須

      .register-value.radios
        label.register-radio-label
          input#type-0.register-radio-form(
            v-model="_value"
            name="filter-type"
            type="radio"
            :value="0"
            v-validate="{ required: true }"
            data-vv-as="予定範囲（利用者グループ）")
          span.register-radio-text 施設全体
        label.register-radio-label
          input#type-1.register-radio-form(v-model="_value" name="filter-type" type="radio" :value="1")
          span.register-radio-text 利用者グループ

      .error-messages(v-for="error in errors.collect('filter-type')")
        label.error-message {{ error }}
        br
  //- select resident group
  .register-row(v-show="1 <= _value")
    .register-group
      .register-key
        span.key-name 利用者グループ
        span.validation-label 必須
      .register-value.flex-wrap.send-targets
        .loading(v-if="isBusy")
          b-spinner.spinner
          span 読込中...
        button.btn.target-select-btn(v-else @click="displayTargetSelectModal") {{ headerTitle }}選択
        .event-target.d-flex(v-for="target in _targetResidentGroups")
          span.target-name.align-self-center {{ target.name }}
          b-badge.delete-btn.align-self-center(variant="secondary" @click="deleteTarget(target)") X
      .error-messages(v-for="error in errors.collect('resident-groups')")
        label.error-message {{ error }}
        br
  
  TargetSelectModal(v-model="showTargetSelectModal" :selectOptions="_selectOptions" :headerTitle="headerTitle" @ok="updateTarget")
</template>

<script lang="ts">
import { Component, Prop, Inject, Vue } from "vue-property-decorator";
import { Validator } from "vee-validate";
import { userStore } from "@/store/store";

import { CalendarFilterType } from "@/commons/services/CalendarEvents";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";

import TargetSelectModal, { TargetSelectModalModel } from "@/pc/components/reports/TargetSelectModal.vue";

@Component({
  components: {
    TargetSelectModal
  }
})
export default class InputFilterType extends Vue {
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  // 親コンポーネントのバリデーターをInjectionする
  @Inject("$validator") $validator!: Validator;

  /** 親コンポーネントでv-modelで指定した値 */
  @Prop()
  value!: CalendarFilterType;
  /** 選択された利用者グループ */
  @Prop()
  filterResidentGroups!: Array<number>;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;

  /** 利用者グループ選択モーダル表示可否 */
  showTargetSelectModal: boolean = false;
  /** 宛先選択モーダルのタイトル */
  headerTitle: string = "利用者グループ";
  /** 利用者グループ */
  residentGroups: Array<ResidentGroupsGetModel> = [];

  /** 子コンポーネントのv-modelでpropの値を指定してはいけないのでgetterにする */
  get _value(): CalendarFilterType {
    return this.value;
  }

  /** 値が変更されたら親コンポーネントに値を渡す */
  set _value(newValue: CalendarFilterType) {
    this.$emit("input", newValue);
    return;
  }

  get _targetResidentGroups(): Array<TargetSelectModalModel> {
    const targets: Array<TargetSelectModalModel> = [];

    const map = new Map();
    for (const groupId of this.filterResidentGroups) {
      map.set(groupId, groupId);
    }

    for (const group of this.residentGroups) {
      if (map.has(group.id)) {
        targets.push({
          id: group.id,
          isChecked: true,
          name: group.name
        });
      }
    }
    return targets;
  }

  get _selectOptions(): Array<TargetSelectModalModel> {
    const targets: Array<TargetSelectModalModel> = [];

    const map = new Map();
    for (const groupId of this.filterResidentGroups) {
      map.set(groupId, groupId);
    }

    for (const group of this.residentGroups) {
      const isChecked = map.has(group.id) ? true : false;
      targets.push({
        id: group.id,
        isChecked,
        name: group.name
      });
    }
    return targets;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;

    const residentGroups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    this.residentGroups = residentGroups;

    this.isBusy = false;

    return;
  }

  /**
   * ### 利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const groups: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return groups;
  }

  /**
   * ### 宛先選択モーダルを表示する
   */
  public displayTargetSelectModal(): void {
    this.showTargetSelectModal = true;
    return;
  }

  /**
   * ### 宛先選択結果を格納する
   */
  public updateTarget(targets: Array<TargetSelectModalModel>): void {
    switch (this._value) {
      case CalendarFilterType.RESIDENT_GROUP: {
        const filterResidentGroups: number[] = [];
        for (const group of targets) {
          if (group.isChecked) {
            filterResidentGroups.push(group.id);
          }
        }
        this.$emit("update:filterResidentGroups", filterResidentGroups);

        break;
      }
    }
    return;
  }

  /**
   * ### 宛先を削除する
   */
  public deleteTarget(target: TargetSelectModalModel): void {
    switch (this._value) {
      case CalendarFilterType.RESIDENT_GROUP: {
        const filterResidentGroups: number[] = [];
        for (const group of this.filterResidentGroups) {
          if (group != target.id) {
            filterResidentGroups.push(group);
          }
        }
        this.$emit("update:filterResidentGroups", filterResidentGroups);

        break;
      }
    }

    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

// ラジオボタンは横幅に応じてラップする
.radios {
  flex-wrap: wrap;
}
.target-select-btn {
  margin: 0 8px 4px 0;
  width: 180px;
  min-width: 180px;
}
.event-target {
  display: inline-block;
  padding: 7px;
  font-size: 16px;
  margin: 0 8px 4px 0;
  border-radius: 3px;
  transition: all 0.2s;
  border: 1px solid $form-border-color;

  .target-name {
    margin: 0 8px 0 0 !important;
  }
  .delete-btn {
    margin: 0 !important;
    font-size: 14px !important;
  }
}
</style>
